import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RECORDING_MAX_DURATION_SEC,
  RECORDING_WARNING_AT_SEC,
} from './constants';
import {
  isRecordingSupported,
  resolveSupportedMimeType,
} from './audio-mime.helper';

export type RecorderStatus =
  | 'idle'
  | 'requesting'
  | 'denied'
  | 'recording'
  | 'stopped';

export interface AudioRecordingResult {
  blob: Blob;
  mimeType: string;
  durationSec: number;
}

interface Options {
  onStopped?: (result: AudioRecordingResult) => void;
  onWarning?: () => void;
  onPermissionDenied?: () => void;
  onError?: (error: unknown) => void;
}

const PERMISSION_ERROR_NAMES = [
  'NotAllowedError',
  'PermissionDeniedError',
  'SecurityError',
];

const isPermissionError = (error: unknown) =>
  error instanceof Error && PERMISSION_ERROR_NAMES.includes(error.name);

export const useAudioRecorder = ({
  onStopped,
  onWarning,
  onPermissionDenied,
  onError,
}: Options = {}) => {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = useRef(0);
  const isCancelledRef = useRef(false);
  const callbacksRef = useRef({
    onStopped,
    onWarning,
    onPermissionDenied,
    onError,
  });

  callbacksRef.current = {
    onStopped,
    onWarning,
    onPermissionDenied,
    onError,
  };

  const clearTimer = useCallback(() => {
    if (!intervalRef.current) return;

    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
    setActiveStream(null);
  }, []);

  const stopRecorder = useCallback(() => {
    clearTimer();

    const recorder = recorderRef.current;

    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
      return;
    }

    releaseStream();
  }, [clearTimer, releaseStream]);

  const stop = useCallback(() => {
    isCancelledRef.current = false;
    stopRecorder();
  }, [stopRecorder]);

  const cancel = useCallback(() => {
    isCancelledRef.current = true;
    stopRecorder();
    setStatus('idle');
    setElapsedSec(0);
    elapsedRef.current = 0;
  }, [stopRecorder]);

  const startTimer = useCallback(() => {
    clearTimer();
    elapsedRef.current = 0;
    setElapsedSec(0);

    intervalRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsedSec(elapsedRef.current);

      if (elapsedRef.current === RECORDING_WARNING_AT_SEC) {
        callbacksRef.current.onWarning?.();
      }

      if (elapsedRef.current >= RECORDING_MAX_DURATION_SEC) {
        isCancelledRef.current = false;
        stopRecorder();
      }
    }, 1000);
  }, [clearTimer, stopRecorder]);

  const start = useCallback(async () => {
    if (!isRecordingSupported()) {
      callbacksRef.current.onError?.(
        new Error('MediaRecorder is not supported in this browser')
      );
      return;
    }

    setStatus('requesting');
    isCancelledRef.current = false;

    let stream: MediaStream;

    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      setStatus(isPermissionError(error) ? 'denied' : 'idle');

      if (isPermissionError(error)) {
        callbacksRef.current.onPermissionDenied?.();
      } else {
        callbacksRef.current.onError?.(error);
      }
      return;
    }

    if (isCancelledRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    try {
      const mimeType = resolveSupportedMimeType();
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );

      chunksRef.current = [];
      streamRef.current = stream;
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        clearTimer();

        const resolvedMimeType = recorder.mimeType || mimeType;
        const chunks = chunksRef.current;
        const durationSec = elapsedRef.current;
        const wasCancelled = isCancelledRef.current;

        chunksRef.current = [];
        releaseStream();

        if (wasCancelled) return;

        setStatus('stopped');
        callbacksRef.current.onStopped?.({
          blob: new Blob(chunks, { type: resolvedMimeType }),
          mimeType: resolvedMimeType,
          durationSec,
        });
      };

      recorder.onerror = (event) => {
        clearTimer();
        releaseStream();
        setStatus('idle');
        callbacksRef.current.onError?.(
          (event as unknown as { error?: unknown }).error ?? event
        );
      };

      recorder.start();
      setStatus('recording');
      setActiveStream(stream);
      startTimer();
    } catch (error) {
      stream.getTracks().forEach((track) => track.stop());
      setStatus('idle');
      callbacksRef.current.onError?.(error);
    }
  }, [clearTimer, releaseStream, startTimer]);

  useEffect(
    () => () => {
      clearTimer();

      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        isCancelledRef.current = true;
        recorder.stop();
      }

      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      recorderRef.current = null;
    },
    [clearTimer]
  );

  return {
    status,
    elapsedSec,
    stream: activeStream,
    isWarning: status === 'recording' && elapsedSec >= RECORDING_WARNING_AT_SEC,
    start,
    stop,
    cancel,
  };
};
